import { createElement, useRef } from "react";
import { X } from "lucide-react";
import { NODE_W, NODE_H, STEP_TYPES, STATE_COLOR } from "@/lib/wf";
import { cn } from "@/lib/utils";

const Edge = ({ a, b, onRemove, editable }) => {
  const x1 = a.x + NODE_W, y1 = a.y + NODE_H / 2, x2 = b.x, y2 = b.y + NODE_H / 2;
  const mx = (x1 + x2) / 2;
  const d = `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`;
  return (
    <g className="group">
      <path d={d} stroke="rgba(212,255,58,0.45)" strokeWidth="1.6" fill="none" />
      <path d={d} stroke="transparent" strokeWidth="14" fill="none" className={editable ? "cursor-pointer" : ""} onClick={editable ? onRemove : undefined} />
    </g>
  );
};

const NodeCard = ({ n, deps, agent, selected, state, onDown, onSelect, onPortOut, onPortIn, linking }) => {
  const t = STEP_TYPES[n.type];
  const hex = n.type === "agent" ? deps[agent?.department]?.hex || t.color : t.color;
  return (
    <div data-testid={`wf-node-${n.id}`} onPointerDown={(e) => onDown(e, n)} onClick={(e) => { e.stopPropagation(); onSelect(n.id); }}
      className={cn("absolute rounded-xl border bg-[#0B0B11] px-3.5 py-2.5 select-none touch-none transition-[border-color,box-shadow] duration-200",
        selected ? "border-white/60" : "border-white/[0.1]", state === "running" && "mh-blink")}
      style={{ left: n.x, top: n.y, width: NODE_W, height: NODE_H, boxShadow: state && state !== "pending" ? `0 0 0 1px ${STATE_COLOR[state]}, 0 0 24px -6px ${STATE_COLOR[state]}` : undefined }}>
      <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: hex }}>
        <span className="w-1.5 h-1.5 rotate-45" style={{ background: hex }} />{t.label}{state && <span className="ml-auto text-muted-foreground normal-case tracking-normal">{state}</span>}
      </div>
      <div className="text-sm font-medium mt-1 truncate">{n.label}</div>
      <div className="text-[11px] text-muted-foreground truncate">{n.type === "agent" ? (agent ? `${deps[agent.department]?.name} · ${agent.stage}` : "No agent picked") : t.hint}</div>
      <button type="button" aria-label="connect in" data-testid={`port-in-${n.id}`} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onPortIn(n.id); }}
        className={cn("absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-[#0B0B11] transition-transform hover:scale-125", linking ? "bg-[#D4FF3A]" : "bg-white/30")} />
      <button type="button" aria-label="connect out" data-testid={`port-out-${n.id}`} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onPortOut(n.id); }}
        className={cn("absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-[#0B0B11] transition-transform hover:scale-125", linking === n.id ? "bg-[#D4FF3A]" : "bg-white/60")} />
    </div>
  );
};

export const WorkflowCanvas = ({ wf, setWf, agentsBy, deps, selected, setSelected, linking, setLinking, runStates, editable, onDropStep }) => {
  const ref = useRef(null);
  const drag = useRef(null);
  const byId = Object.fromEntries(wf.nodes.map((n) => [n.id, n]));

  const onDown = (e, n) => {
    if (!editable) return;
    drag.current = { id: n.id, sx: e.clientX, sy: e.clientY, ox: n.x, oy: n.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const x = Math.max(0, d.ox + e.clientX - d.sx), y = Math.max(0, d.oy + e.clientY - d.sy);
    setWf((w) => ({ ...w, nodes: w.nodes.map((n) => (n.id === d.id ? { ...n, x, y } : n)) }));
  };
  const onDrop = (e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("application/mh-step");
    if (!raw || !editable) return;
    const rect = ref.current.getBoundingClientRect();
    onDropStep(JSON.parse(raw), e.clientX - rect.left + ref.current.scrollLeft - NODE_W / 2, e.clientY - rect.top + ref.current.scrollTop - NODE_H / 2);
  };
  const portOut = (id) => editable && setLinking(linking === id ? null : id);
  const portIn = (id) => {
    if (!editable || !linking || linking === id) return;
    setWf((w) => ({ ...w, edges: [...w.edges.filter((e) => e.source !== linking && e.target !== id), { id: `e_${Date.now()}`, source: linking, target: id }] }));
    setLinking(null);
  };
  const maxX = Math.max(900, ...wf.nodes.map((n) => n.x + NODE_W + 120));
  const maxY = Math.max(480, ...wf.nodes.map((n) => n.y + NODE_H + 120));

  return (
    <div ref={ref} className="relative w-full h-full overflow-auto rounded-xl border border-white/[0.07] bg-[#06060A]" data-testid="wf-canvas"
      onDragOver={(e) => e.preventDefault()} onDrop={onDrop} onPointerMove={onMove} onPointerUp={() => (drag.current = null)} onClick={() => { setSelected(null); setLinking(null); }}
      style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)", backgroundSize: "22px 22px" }}>
      <div className="relative" style={{ width: maxX, height: maxY }}>
        {createElement("svg", { className: "absolute inset-0 pointer-events-none", width: maxX, height: maxY },
          // Built without JSX children: the preview editor wraps dynamic JSX children in <span>, which hides SVG content.
          createElement("g", { className: "pointer-events-auto" }, ...wf.edges.filter((e) => byId[e.source] && byId[e.target]).map((e) => createElement(Edge, {
            key: e.id, a: byId[e.source], b: byId[e.target], editable,
            onRemove: (ev) => { ev.stopPropagation(); setWf((w) => ({ ...w, edges: w.edges.filter((x) => x.id !== e.id) })); } }))))}
        {wf.nodes.map((n) => (
          <NodeCard key={n.id} n={n} deps={deps} agent={agentsBy[n.agent_slug]} selected={selected === n.id} state={runStates?.[n.id]} linking={linking}
            onDown={onDown} onSelect={setSelected} onPortOut={portOut} onPortIn={portIn} />
        ))}
        {wf.nodes.length === 0 && <div className="absolute inset-0 grid place-items-center text-sm text-muted-foreground pointer-events-none">Drag a step or agent here to start</div>}
        {linking && <div className="absolute top-3 left-3 flex items-center gap-2 px-3 h-8 rounded-full bg-[#D4FF3A] text-black text-xs font-medium" data-testid="wf-linking-hint">Click a step's left dot to connect<X className="w-3.5 h-3.5 cursor-pointer" onClick={(e) => { e.stopPropagation(); setLinking(null); }} /></div>}
      </div>
    </div>
  );
};
